import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import Contact from '../../components/Contact';
import WhatsAppButton from '../../components/WhatsAppButton';
import VideoTestimonials from '../../components/VideoTestimonials';
import CTA from '../../components/CTA';
import HomeBrandBanner from '../../components/HomeBrandBanner';
import { FiArrowRight, FiCheckSquare } from 'react-icons/fi';
import { useSiteContent } from '../../context/SiteContentContext';

import { 
  FiMonitor, FiSmartphone, FiZap, FiCloud, FiLink, FiBriefcase, FiTarget, 
  FiLock, FiStar, FiEdit3, FiSend, FiSearch, FiGrid, FiSettings, 
  FiShoppingCart, FiFileText, FiEye, FiBarChart2, FiTrendingUp, FiVolume2, 
  FiDollarSign, FiUsers, FiCpu, FiVideo, FiPrinter, 
  FiTool, FiRefreshCw, FiFolder, FiAward
} from 'react-icons/fi';
import { 
  TbRobot, TbBrain
} from 'react-icons/tb';

// ─── Shared services list (used for "Related Services" section) ───────────
export const ALL_SERVICES = [
  {
    name: 'Web & Mobile Apps Development',
    href: '/services/web-mobile-apps',
    description: 'Custom web and mobile applications built to scale.',
  },
  {
    name: 'CMS / Ecommerce Development',
    href: '/services/cms-ecommerce',
    description: 'Powerful CMS and ecommerce platforms that sell.',
  },
  {
    name: 'Digital Marketing & Strategy',
    href: '/services/digital-marketing',
    description: 'Data-driven campaigns that drive real ROI.',
  },
  {
    name: 'Graphic Design & Video Editing',
    href: '/services/graphic-design',
    description: 'Stunning visuals that make your brand unforgettable.',
  },
  {
    name: 'UI/UX Design',
    href: '/services/ui-ux-design',
    description: 'User-first designs that convert visitors to customers.',
  },
  {
    name: 'AI-Powered Technologies',
    href: '/services/ai-technologies',
    description: 'Automate and innovate with cutting-edge AI solutions.',
  },
];

// ─── Default 8-step project cycle (shared across all pages) ──────────────
const DEFAULT_PROJECT_CYCLE = [
  {
    title: 'Discovery & Planning',
    description:
      'We start by understanding your business goals, target audience, and technical requirements to build a solid project foundation.',
  },
  {
    title: 'Wireframes & UI/UX Design',
    description:
      'Our designers create detailed wireframes and mockups illustrating all features, user flows, and interface functionalities.',
  },
  {
    title: 'Development & Coding',
    description:
      'Once designs are approved, our developers write clean, high-quality, scalable code tailored to your unique requirements.',
  },
  {
    title: 'Quality Assurance Testing',
    description:
      'The product undergoes rigorous quality testing across all devices and platforms. Once approved, it is ready for launch.',
  },
  {
    title: 'SEO Optimization',
    description:
      'To ensure maximum reach and business growth, we perform comprehensive SEO optimization before and after launch.',
  },
  {
    title: 'Client Review & Revisions',
    description:
      'Necessary changes and corrections are made per your specifications to ensure the final product meets all expectations.',
  },
  {
    title: 'Prototype Demo',
    description:
      'We value your feedback, refining the application iteratively to ensure it aligns perfectly with your vision and business goals.',
  },
  {
    title: 'Deployment & Maintenance',
    description:
      'Client satisfaction is paramount at RojrzTech. We provide ongoing maintenance and support to monitor performance and security.',
  },
];

// ─── Emojis to React Icons mapping ───────────────────────────────────────
const iconMap = {
  '💻': <FiMonitor />,
  '📱': <FiSmartphone />,
  '⚡': <FiZap />,
  '☁️': <FiCloud />,
  '🔗': <FiLink />,
  '🏢': <FiBriefcase />,
  '🎯': <FiTarget />,
  '🔒': <FiLock />,
  '⭐': <FiStar />,
  '✏️': <FiEdit3 />,
  '🚀': <FiSend />,
  '🔬': <FiSearch />,
  '📐': <FiSettings />,
  '🎨': <FiGrid />,
  '🖥️': <FiMonitor />,
  '⚙️': <FiSettings />,
  '🛒': <FiShoppingCart />,
  '🏪': <FiShoppingCart />,
  '📝': <FiFileText />,
  '🤖': <TbRobot />,
  '🧠': <TbBrain />,
  '👁️': <FiEye />,
  '📊': <FiBarChart2 />,
  '📈': <FiTrendingUp />,
  '📣': <FiVolume2 />,
  '🔍': <FiSearch />,
  '💵': <FiDollarSign />,
  '👥': <FiUsers />,
  '🔌': <FiCpu />,
  '💡': <FiZap />,
  '🎬': <FiVideo />,
  '✨': <FiAward />,
  '🖨️': <FiPrinter />,
  '🔧': <FiTool />,
  '🔄': <FiRefreshCw />,
  '📁': <FiFolder />,
  '🌟': <FiAward />,
};

const getIcon = (emoji, className = '') => {
  const icon = iconMap[emoji] || <FiStar />;
  return React.cloneElement(icon, { className: `${className} flex-shrink-0` });
};

// ═══════════════════════════════════════════════════════════
//  RADIAL SHOWCASE COMPONENT (Web & Mobile Apps)
// ═══════════════════════════════════════════════════════════
const WebAppsRadialShowcase = () => {
  const [activeCard, setActiveCard] = useState(0);

  const radialCards = [
    {
      title: 'Native iOS & Android Apps',
      desc: 'High performance native applications built using Swift, Kotlin, and Java.',
      icon: '📱',
    },
    {
      title: 'Progressive Web Apps',
      desc: 'Fast, secure, and installable web applications that run offline.',
      icon: '💻',
    },
    {
      title: 'DevOps & Cloud Development',
      desc: 'Automated CI/CD pipelines, Docker containerization, and AWS/GCP cloud deployments.',
      icon: '☁️',
    },
    {
      title: 'Real-Time Dashboards & Systems',
      desc: 'Low-latency real-time web applications with live charts, WebSockets, and database sync.',
      icon: '📊',
    },
    {
      title: 'Cross-Platform Mobile Apps',
      desc: 'Single codebase apps for iOS and Android built with Flutter or React Native.',
      icon: '⚡',
    },
  ];

  return (
    <section className="py-24 relative overflow-hidden bg-transparent">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F2B135]/30 bg-[#F2B135]/10 text-[#F2B135] text-xs font-bold uppercase mb-6">
            <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-pulse" />
            Interactive Core Architecture
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            App Ecosystem & Cloud Integration
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Click on any module to preview the app layout and details in real-time.
          </p>
        </div>

        {/* Desktop Radial Grid (lg and up) */}
        <div className="hidden lg:flex items-center justify-center min-h-[650px] relative w-full">
          {/* Orbital path rings */}
          <div className="absolute w-[620px] h-[620px] border border-white/5 rounded-full pointer-events-none" />
          <div className="absolute w-[820px] h-[820px] border border-white/5 rounded-full pointer-events-none" />
          <div className="absolute w-[1020px] h-[1020px] border border-white/5 rounded-full pointer-events-none animate-[spin_120s_linear_infinite]" />

          {/* Central phone mockup */}
          <div className="relative w-[300px] h-[580px] bg-[#0c0c0c] border-[10px] border-zinc-800 rounded-[50px] overflow-hidden shadow-2xl flex flex-col p-3 z-20 transition-all duration-500 hover:border-[#F2B135]/40 hover:shadow-[0_0_50px_rgba(242,177,53,0.15)]">
            {/* Phone notch */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-full z-30 flex items-center justify-center">
              <div className="w-3 h-3 rounded-full bg-zinc-800 mr-2" />
              <div className="w-8 h-1 bg-zinc-900 rounded-full" />
            </div>

            {/* Screen Content */}
            <div className="flex-1 bg-black rounded-[38px] p-6 flex flex-col justify-between overflow-hidden relative pt-10">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#F2B135]/10 rounded-full blur-2xl" />
              
              {/* Header */}
              <div className="flex justify-between items-center text-xs text-white/50 mb-6">
                <span>RojrzTech Preview</span>
                <span className="text-[#F2B135] font-bold">● Live State</span>
              </div>

              {/* Layout inside phone preview */}
              <div className="flex-1 flex flex-col justify-center items-center text-center space-y-4">
                <span className="text-5xl text-[#F2B135] animate-bounce w-16 h-16 flex items-center justify-center">
                  {getIcon(radialCards[activeCard].icon, "w-12 h-12")}
                </span>
                <h4 className="text-xl font-bold text-white leading-tight">
                  {radialCards[activeCard].title}
                </h4>
                <p className="text-white/60 text-xs leading-relaxed max-w-[220px]">
                  {radialCards[activeCard].desc}
                </p>
              </div>

              {/* Footer Button */}
              <div className="mt-6">
                <button className="w-full py-3 rounded-full bg-[#F2B135] text-black font-extrabold text-xs uppercase tracking-wider hover:bg-white hover:text-black transition-colors shadow-[0_0_15px_rgba(242,177,53,0.3)]">
                  Launch Demo
                </button>
              </div>
            </div>
          </div>

          {/* Radially positioned cards */}
          {/* Top Center Card */}
          <div
            onClick={() => setActiveCard(0)}
            className={`absolute top-[0%] w-[240px] p-5 rounded-2xl border transition-all duration-300 cursor-pointer text-center backdrop-blur-xl ${
              activeCard === 0
                ? 'bg-[#F2B135]/10 border-[#F2B135] shadow-[0_0_30px_rgba(242,177,53,0.15)] scale-105'
                : 'bg-white/[0.02] border-white/10 hover:border-white/30'
            }`}
          >
            <span className="text-2xl text-[#F2B135] block mb-2 mx-auto w-8 h-8 flex justify-center">{getIcon(radialCards[0].icon, "w-6 h-6")}</span>
            <h3 className="text-white font-bold text-sm mb-1">{radialCards[0].title}</h3>
            <p className="text-white/40 text-xs">{radialCards[0].desc.slice(0, 50)}...</p>
          </div>

          {/* Middle Left Card */}
          <div
            onClick={() => setActiveCard(1)}
            className={`absolute left-[0%] lg:left-[5%] top-[28%] w-[240px] p-5 rounded-2xl border transition-all duration-300 cursor-pointer text-center backdrop-blur-xl ${
              activeCard === 1
                ? 'bg-[#F2B135]/10 border-[#F2B135] shadow-[0_0_30px_rgba(242,177,53,0.15)] scale-105'
                : 'bg-white/[0.02] border-white/10 hover:border-white/30'
            }`}
          >
            <span className="text-2xl text-[#F2B135] block mb-2 mx-auto w-8 h-8 flex justify-center">{getIcon(radialCards[1].icon, "w-6 h-6")}</span>
            <h3 className="text-white font-bold text-sm mb-1">{radialCards[1].title}</h3>
            <p className="text-white/40 text-xs">{radialCards[1].desc.slice(0, 50)}...</p>
          </div>

          {/* Middle Right Card */}
          <div
            onClick={() => setActiveCard(2)}
            className={`absolute right-[0%] lg:right-[5%] top-[28%] w-[240px] p-5 rounded-2xl border transition-all duration-300 cursor-pointer text-center backdrop-blur-xl ${
              activeCard === 2
                ? 'bg-[#F2B135]/10 border-[#F2B135] shadow-[0_0_30px_rgba(242,177,53,0.15)] scale-105'
                : 'bg-white/[0.02] border-white/10 hover:border-white/30'
            }`}
          >
            <span className="text-2xl text-[#F2B135] block mb-2 mx-auto w-8 h-8 flex justify-center">{getIcon(radialCards[2].icon, "w-6 h-6")}</span>
            <h3 className="text-white font-bold text-sm mb-1">{radialCards[2].title}</h3>
            <p className="text-white/40 text-xs">{radialCards[2].desc.slice(0, 50)}...</p>
          </div>

          {/* Bottom Left Card */}
          <div
            onClick={() => setActiveCard(3)}
            className={`absolute left-[0%] lg:left-[8%] top-[68%] w-[240px] p-5 rounded-2xl border transition-all duration-300 cursor-pointer text-center backdrop-blur-xl ${
              activeCard === 3
                ? 'bg-[#F2B135]/10 border-[#F2B135] shadow-[0_0_30px_rgba(242,177,53,0.15)] scale-105'
                : 'bg-white/[0.02] border-white/10 hover:border-white/30'
            }`}
          >
            <span className="text-2xl text-[#F2B135] block mb-2 mx-auto w-8 h-8 flex justify-center">{getIcon(radialCards[3].icon, "w-6 h-6")}</span>
            <h3 className="text-white font-bold text-sm mb-1">{radialCards[3].title}</h3>
            <p className="text-white/40 text-xs">{radialCards[3].desc.slice(0, 50)}...</p>
          </div>

          {/* Bottom Right Card */}
          <div
            onClick={() => setActiveCard(4)}
            className={`absolute right-[0%] lg:right-[8%] top-[68%] w-[240px] p-5 rounded-2xl border transition-all duration-300 cursor-pointer text-center backdrop-blur-xl ${
              activeCard === 4
                ? 'bg-[#F2B135]/10 border-[#F2B135] shadow-[0_0_30px_rgba(242,177,53,0.15)] scale-105'
                : 'bg-white/[0.02] border-white/10 hover:border-white/30'
            }`}
          >
            <span className="text-2xl text-[#F2B135] block mb-2 mx-auto w-8 h-8 flex justify-center">{getIcon(radialCards[4].icon, "w-6 h-6")}</span>
            <h3 className="text-white font-bold text-sm mb-1">{radialCards[4].title}</h3>
            <p className="text-white/40 text-xs">{radialCards[4].desc.slice(0, 50)}...</p>
          </div>
        </div>

        {/* Mobile Swipeable Slider / Carousel (less than lg) */}
        <div className="lg:hidden flex flex-col space-y-6">
          <div className="flex gap-4 overflow-x-auto pb-6 scrollbar-thin scrollbar-thumb-zinc-800 scroll-smooth snap-x snap-mandatory">
            {radialCards.map((card, i) => (
              <div
                key={i}
                onClick={() => setActiveCard(i)}
                className={`snap-center flex-shrink-0 w-[280px] p-6 rounded-2xl border transition-all duration-300 text-center backdrop-blur-xl ${
                  activeCard === i
                    ? 'bg-[#F2B135]/10 border-[#F2B135] shadow-lg'
                    : 'bg-white/[0.02] border-white/10'
                }`}
              >
                <span className="text-4xl text-[#F2B135] block mb-3 mx-auto w-10 h-10 flex justify-center">{getIcon(card.icon, "w-10 h-10")}</span>
                <h3 className="text-white font-bold text-lg mb-2">{card.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>

          {/* Active slide dots */}
          <div className="flex justify-center gap-2">
            {radialCards.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveCard(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === activeCard ? 'bg-[#F2B135] w-6' : 'bg-white/20 w-2'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════
//  CMS & ECOMMERCE COMPONENT (Platforms & Benefits Showcase)
// ═══════════════════════════════════════════════════════════
const CmsPlatformsAndBenefitsShowcase = () => {
  const [activePlatform, setActivePlatform] = useState(0);

  const platforms = [
    {
      name: 'Shopify',
      logo: 'https://cdn.worldvectorlogo.com/logos/shopify.svg',
      glow: 'shadow-[0_0_30px_rgba(150,191,72,0.25)] border-[#96bf48]/40 hover:border-[#96bf48]',
      activeGlow: 'bg-[#96bf48]/10 border-[#96bf48] shadow-[0_0_40px_rgba(150,191,72,0.4)]',
      desc: 'Best-in-class e-commerce platform. Fully scalable, secure, and optimized for global online shops with custom themes and app integrations.',
    },
    {
      name: 'WordPress',
      logo: 'https://cdn.worldvectorlogo.com/logos/wordpress-blue.svg',
      glow: 'shadow-[0_0_30px_rgba(33,117,155,0.25)] border-[#21759b]/40 hover:border-[#21759b]',
      activeGlow: 'bg-[#21759b]/10 border-[#21759b] shadow-[0_0_40px_rgba(33,117,155,0.4)]',
      desc: "The world's most popular CMS. We build bespoke custom themes, plugins, and headless CMS integrations giving you maximum control.",
    },
    {
      name: 'Webflow',
      logo: 'https://cdn.worldvectorlogo.com/logos/webflow.svg',
      glow: 'shadow-[0_0_30px_rgba(20,110,245,0.25)] border-[#146ef5]/40 hover:border-[#146ef5]',
      activeGlow: 'bg-[#146ef5]/10 border-[#146ef5] shadow-[0_0_40px_rgba(20,110,245,0.4)]',
      desc: 'Next-generation design-first CMS. Lightning-fast load speeds, premium animations, and pixel-perfect design capabilities built for modern brands.',
    },
    {
      name: 'Magento',
      logo: 'https://cdn.worldvectorlogo.com/logos/magento.svg',
      glow: 'shadow-[0_0_30px_rgba(244,111,37,0.25)] border-[#f46f25]/40 hover:border-[#f46f25]',
      activeGlow: 'bg-[#f46f25]/10 border-[#f46f25] shadow-[0_0_40px_rgba(244,111,37,0.4)]',
      desc: 'Enterprise-level ecommerce platform. Robust multi-store handling, deep customization potentials, and advanced catalog control systems.',
    },
    {
      name: 'WooCommerce',
      logo: 'https://cdn.worldvectorlogo.com/logos/woocommerce.svg',
      glow: 'shadow-[0_0_30px_rgba(150,88,138,0.25)] border-[#96588a]/40 hover:border-[#96588a]',
      activeGlow: 'bg-[#96588a]/10 border-[#96588a] shadow-[0_0_40px_rgba(150,88,138,0.4)]',
      desc: 'Perfect integration for WordPress. Blends the powerful content management of WordPress with an incredibly customizable shopping cart system.',
    },
  ];

  const benefits = [
    { title: 'Multi-Device Support', icon: '📱' },
    { title: 'CMS Solutions', icon: '🔧' },
    { title: 'Custom Development', icon: '🛒' },
    { title: 'E-Commerce Platforms', icon: '🎯' },
    { title: 'Conversion Optimized', icon: '📈' },
  ];

  return (
    <section className="py-24 relative overflow-hidden bg-transparent">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* PLATFORMS GRID (Image 3 Style) */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Top Platforms. High-Performance Websites.
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            We specialize in all major CMS and eCommerce platforms. Select a platform to see our services:
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
          {platforms.map((p, i) => (
            <div
              key={i}
              onClick={() => setActivePlatform(i)}
              className={`p-6 rounded-2xl border backdrop-blur-xl flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-300 hover:-translate-y-1 ${
                activePlatform === i ? p.activeGlow : `bg-white/[0.02] border-white/10 ${p.glow}`
              }`}
            >
              <img src={p.logo} alt={p.name} className="h-10 mb-4 object-contain brightness-95" />
              <span className="text-white font-bold text-sm">{p.name}</span>
            </div>
          ))}
        </div>

        {/* Selected platform description */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-8 max-w-4xl mx-auto text-center backdrop-blur-md mb-24 shadow-2xl">
          <h4 className="text-2xl font-black text-[#F2B135] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Our {platforms[activePlatform].name} Expertise
          </h4>
          <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-2xl mx-auto font-medium">
            {platforms[activePlatform].desc}
          </p>
          <div className="mt-6 flex justify-center">
            <Link to="/contact" className="btn-animated-gold">
              Build {platforms[activePlatform].name} Store
            </Link>
          </div>
        </div>

        {/* HORIZONTAL BENEFITS TIMELINE (Image 2 Style) */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Engineered For Absolute Quality
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Our ecommerce sites are structured around critical business values.
          </p>
        </div>

        {/* Timeline layout */}
        <div className="relative pt-12 pb-16 hidden md:block">
          <div className="grid grid-cols-5 gap-4 relative">
            {benefits.map((b, i) => (
              <div key={i} className="flex flex-col items-center group relative cursor-default">
                {/* Icon Circle badge */}
                <div className="w-16 h-16 rounded-full bg-white/[0.03] border border-white/15 flex items-center justify-center text-2xl mb-8 group-hover:bg-[#F2B135]/10 group-hover:border-[#F2B135] group-hover:scale-110 transition-all duration-300 shadow-lg text-[#F2B135]">
                  {getIcon(b.icon, "w-6 h-6")}
                </div>
                
                {/* Vertical connecting line */}
                <div className="w-[1px] h-12 bg-white/10 group-hover:bg-[#F2B135]/50 transition-colors" />

                {/* Title */}
                <span className="text-white font-bold text-center text-sm group-hover:text-[#F2B135] transition-colors pt-4 leading-tight">
                  {b.title}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile vertical version */}
        <div className="md:hidden flex flex-col space-y-6">
          {benefits.map((b, i) => (
            <div key={i} className="flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
              <div className="w-12 h-12 rounded-full bg-[#F2B135]/10 border border-[#F2B135]/30 flex items-center justify-center text-xl flex-shrink-0 text-[#F2B135]">
                {getIcon(b.icon, "w-6 h-6")}
              </div>
              <span className="text-white font-bold text-base">{b.title}</span>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════
//  DIGITAL MARKETING COMPONENT (Marketing Strategy Timeline)
// ═══════════════════════════════════════════════════════════
const DigitalMarketingTimeline = () => {
  const marketingServices = [
    { title: 'Influencer Marketing', desc: 'Expand reach and build authentic brand trust with strategic collaborations.', icon: '📣' },
    { title: 'SEO Services', desc: 'Improve search visibility, ranking authority, and attract high-intent organic traffic.', icon: '🔍' },
    { title: 'PPC Advertising', desc: 'Generate immediate high-converting traffic with targeted, measurable ad campaigns.', icon: '💵' },
    { title: 'Social Media Marketing', desc: 'Grow loyal community engagement and strengthen visual brand presence.', icon: '👥' },
    { title: 'Content Marketing', desc: 'Attract, engage, and retain the right target audience with value-first content.', icon: '📝' },
  ];

  return (
    <section className="py-24 relative overflow-hidden bg-transparent">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F2B135]/30 bg-[#F2B135]/10 text-[#F2B135] text-xs font-bold uppercase mb-6">
            <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-pulse" />
            Strategic Growth Timeline
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Our Core Marketing Channels
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            We leverage data-driven channels to scale your brand presence and conversion rates.
          </p>
        </div>

        {/* Desktop Timeline layout */}
        <div className="relative pt-12 pb-16 hidden md:block">
          <div className="grid grid-cols-5 gap-4 relative">
            {marketingServices.map((s, i) => (
              <div key={i} className="flex flex-col items-center group relative cursor-default">
                {/* Glass Card content on top */}
                <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-8 text-center backdrop-blur-md group-hover:border-[#F2B135]/40 group-hover:bg-[#F2B135]/5 transition-all duration-300 group-hover:-translate-y-2 shadow-xl min-h-[220px]">
                  <span className="text-3xl text-[#F2B135] block mb-3 group-hover:scale-110 transition-transform w-8 h-8 mx-auto flex justify-center">
                    {getIcon(s.icon, "w-8 h-8")}
                  </span>
                  <h3 className="text-white font-bold text-sm mb-2 group-hover:text-[#F2B135] transition-colors">{s.title}</h3>
                  <p className="text-white/40 text-xs leading-relaxed group-hover:text-white/70 transition-colors font-medium">{s.desc}</p>
                </div>
                
                {/* Connecting Line */}
                <div className="w-[1px] h-10 bg-white/10 group-hover:bg-[#F2B135]/40 transition-colors" />
              </div>
            ))}
          </div>
        </div>

        {/* Mobile vertical list */}
        <div className="md:hidden flex flex-col space-y-6">
          {marketingServices.map((s, i) => (
            <div key={i} className="flex gap-4 p-6 rounded-2xl border border-white/10 bg-white/[0.02]">
              <div className="w-12 h-12 rounded-full bg-[#F2B135]/10 border border-[#F2B135]/30 flex items-center justify-center text-2xl flex-shrink-0 text-[#F2B135]">
                {getIcon(s.icon, "w-6 h-6")}
              </div>
              <div>
                <h3 className="text-white font-bold text-base mb-1">{s.title}</h3>
                <p className="text-white/50 text-xs leading-relaxed font-medium">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ═══════════════════════════════════════════════════════════
//  MAIN SERVICE PAGE TEMPLATE
// ═══════════════════════════════════════════════════════════
const ServicePageTemplate = ({ pageData }) => {
  const { siteContent } = useSiteContent();
  const currentPath = window.location.pathname;

  const serviceKeyMap = {
    '/services/web-mobile-apps': 'svc-web-mobile',
    '/services/cms-ecommerce': 'svc-cms-ecom',
    '/services/digital-marketing': 'svc-digital-mkt',
    '/services/graphic-design': 'svc-graphic-video',
    '/services/ui-ux-design': 'svc-ui-ux',
    '/services/ai-technologies': 'svc-ai-tech',
  };

  const dbKey = serviceKeyMap[currentPath];
  const dbContent = dbKey && siteContent ? siteContent[dbKey] : null;

  const { seo, hero, servicesCards, features, excellence, relatedServices } = pageData;
  const projectCycle = pageData.projectCycle || DEFAULT_PROJECT_CYCLE;

  // ── Set page title + meta description ──
  useEffect(() => {
    if (seo?.title) document.title = seo.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && seo?.description) metaDesc.setAttribute('content', seo.description);
    return () => {
      document.title = 'RojrzTech — Digital Agency';
    };
  }, [seo]);

  if (!pageData || !hero?.slides?.length) return null;

  const slide = hero.slides[0];

  return (
    <div className="text-white min-h-screen relative z-10 bg-transparent">
      <Navbar />

      {/* ════════════════════════════════════════
          HERO — Dynamic Banner with Video Background
      ════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
        {/* Video BG */}
        <div className="absolute inset-0 z-0">
          <video autoPlay loop muted playsInline className="object-cover w-full h-full">
            <source src="/herovideo.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/65" />
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black to-transparent z-10" />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* ─── LEFT COLUMN ─── */}
            <div className="flex flex-col items-start transition-all duration-500">
              {/* Badge Pill */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F2B135]/30 bg-[#F2B135]/10 text-[#F2B135] text-xs font-bold uppercase mb-6 backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-pulse" />
                {dbContent?.badge || hero.badge}
              </div>

              {/* Heading */}
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.1] mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {dbContent?.title ? dbContent.title : (
                  <>
                    {slide.heading.pre && <>{slide.heading.pre} </>}
                    {slide.heading.gold && (
                      <span className="text-[#F2B135]">{slide.heading.gold}</span>
                    )}
                    {slide.heading.post && <> {slide.heading.post}</>}
                  </>
                )}
              </h1>

              {/* Subheading */}
              <p className="text-white/85 text-base sm:text-lg leading-relaxed mb-8 max-w-xl font-medium">
                {dbContent?.subtitle || slide.subheading}
              </p>

              {/* Pill Tags */}
              <div className="flex flex-wrap gap-2 mb-10">
                {slide.pills.map((pill, i) => (
                  <span
                    key={i}
                    className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-white text-sm font-semibold backdrop-blur-sm hover:bg-[#F2B135]/20 hover:border-[#F2B135] transition-all duration-200 cursor-default"
                  >
                    {pill}
                  </span>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-5">
                <Link to="/contact" className="btn-animated-gold px-10 py-4 text-base">
                  Start Your Project <FiArrowRight size={16} />
                </Link>
                <Link
                  to="/portfolio"
                  className="flex items-center gap-3 px-8 py-4 rounded-full text-white border border-white/20 hover:bg-white/10 transition-all text-base font-semibold"
                >
                  See Our Work
                </Link>
              </div>
            </div>

            {/* ─── RIGHT COLUMN ─── */}
            <div className="relative mt-10 lg:mt-0 flex justify-end">
              <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl max-w-lg w-full group">
                <div className="absolute -inset-1.5 bg-gradient-to-r from-[#F2B135]/20 to-[#ffaa00]/10 rounded-[28px] blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300" />
                <img
                  src={slide.image}
                  alt={`${slide.heading.pre || ''} ${slide.heading.gold || ''}`}
                  className="w-full object-cover rounded-3xl"
                  style={{ minHeight: '320px', maxHeight: '480px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>

              {/* Rating float card */}
              <div className="absolute top-0 -right-4 z-20 bg-black/60 backdrop-blur-xl border border-[#F2B135]/40 rounded-2xl p-4 flex items-center gap-4 shadow-xl">
                <div className="w-10 h-10 rounded-full bg-[#F2B135] flex items-center justify-center text-black font-black text-lg">
                  ★
                </div>
                <div>
                  <div className="text-white font-bold">4.9/5</div>
                  <div className="text-white/60 text-xs">Client Rating</div>
                </div>
              </div>

              {/* Clients float card */}
              <div className="absolute bottom-10 -left-4 z-20 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 flex items-center gap-4 shadow-xl">
                <div className="text-white font-bold text-sm">Happy Clients</div>
                <div className="text-[#F2B135] font-black ml-2">+2k</div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <main className="relative z-10 w-full bg-transparent">

        {/* ════════════════════════════════════════
            DOUBLE-COLUMN DETAILS SECTION 1 (Slide 2 Content)
        ════════════════════════════════════════ */}
        {hero.slides[1] && (
          <section className="py-20 relative bg-transparent overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 relative z-10">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                {/* Left: Image */}
                <div className="relative group">
                  <div className="absolute -inset-2 bg-gradient-to-r from-[#F2B135]/30 to-[#ffaa00]/10 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500" />
                  <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                    <img
                      src={hero.slides[1].image}
                      alt={hero.slides[1].heading.gold || 'Service Detail'}
                      className="w-full object-cover group-hover:scale-105 transition-transform duration-500 rounded-3xl"
                      style={{ minHeight: '350px', maxHeight: '480px' }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>
                </div>

                {/* Right: Content */}
                <div className="flex flex-col items-start">
                  <h2
                    className="text-3xl md:text-4xl font-black text-white leading-tight mb-6"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {hero.slides[1].heading.pre && <>{hero.slides[1].heading.pre} </>}
                    {hero.slides[1].heading.gold && (
                      <span className="text-[#F2B135]">{hero.slides[1].heading.gold}</span>
                    )}
                    {hero.slides[1].heading.post && <> {hero.slides[1].heading.post}</>}
                  </h2>
                  <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8 max-w-xl font-medium">
                    {hero.slides[1].subheading}
                  </p>
                  {/* Pills */}
                  <div className="flex flex-wrap gap-2 mb-8">
                    {hero.slides[1].pills.map((pill, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/90 text-sm font-semibold hover:border-[#F2B135]/40 transition-colors cursor-default"
                      >
                        {pill}
                      </span>
                    ))}
                  </div>
                  <Link to="/contact" className="btn-animated-gold">
                    Explore Details <FiArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ════════════════════════════════════════
            DOUBLE-COLUMN DETAILS SECTION 2 (Slide 3 Content)
        ════════════════════════════════════════ */}
        {hero.slides[2] && (
          <section className="py-20 relative bg-transparent overflow-hidden">
            <div className="max-w-7xl mx-auto px-6 relative z-10">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                {/* Left: Content */}
                <div className="flex flex-col items-start order-2 lg:order-1">
                  <h2
                    className="text-3xl md:text-4xl font-black text-white leading-tight mb-6"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {hero.slides[2].heading.pre && <>{hero.slides[2].heading.pre} </>}
                    {hero.slides[2].heading.gold && (
                      <span className="text-[#F2B135]">{hero.slides[2].heading.gold}</span>
                    )}
                    {hero.slides[2].heading.post && <> {hero.slides[2].heading.post}</>}
                  </h2>
                  <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8 max-w-xl font-medium">
                    {hero.slides[2].subheading}
                  </p>
                  {/* Pills */}
                  <div className="flex flex-wrap gap-2 mb-8">
                    {hero.slides[2].pills.map((pill, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/90 text-sm font-semibold hover:border-[#F2B135]/40 transition-colors cursor-default"
                      >
                        {pill}
                      </span>
                    ))}
                  </div>
                  <Link to="/contact" className="btn-animated-gold">
                    Free Strategy Call <FiArrowRight size={16} />
                  </Link>
                </div>

                {/* Right: Image */}
                <div className="relative group order-1 lg:order-2">
                  <div className="absolute -inset-2 bg-gradient-to-r from-[#F2B135]/30 to-[#ffaa00]/10 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500" />
                  <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                    <img
                      src={hero.slides[2].image}
                      alt={hero.slides[2].heading.gold || 'Service Detail'}
                      className="w-full object-cover group-hover:scale-105 transition-transform duration-500 rounded-3xl"
                      style={{ minHeight: '350px', maxHeight: '480px' }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ════════════════════════════════════════
            INTERACTIVE REFERENCE LAYOUTS (Based on Active Page)
        ════════════════════════════════════════ */}
        {currentPath === '/services/web-mobile-apps' && <WebAppsRadialShowcase />}
        {currentPath === '/services/cms-ecommerce' && <CmsPlatformsAndBenefitsShowcase />}
        {currentPath === '/services/digital-marketing' && <DigitalMarketingTimeline />}

        {/* ════════════════════════════════════════
            SECTION 3 — Services Cards
        ════════════════════════════════════════ */}
        <section className="py-24 relative bg-transparent">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F2B135]/30 bg-[#F2B135]/10 text-[#F2B135] text-xs font-bold uppercase mb-6">
                <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-pulse" />
                What We Offer
              </div>
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {servicesCards.title}
              </h2>
              <p className="text-white/60 text-base md:text-lg max-w-3xl mx-auto leading-relaxed font-medium">
                {servicesCards.subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {servicesCards.cards.map((card, i) => (
                <div
                  key={i}
                  className="group bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] rounded-2xl p-8 hover:border-[#F2B135]/40 hover:bg-[#F2B135]/5 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(0,0,0,0.5),0_0_30px_rgba(242,177,53,0.08)] cursor-default text-center"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#F2B135]/10 border border-[#F2B135]/20 flex items-center justify-center text-[#F2B135] text-2xl mb-6 mx-auto group-hover:bg-[#F2B135] group-hover:text-black group-hover:scale-110 transition-all duration-300">
                    {getIcon(card.icon, "w-6 h-6")}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-4 group-hover:text-[#F2B135] transition-colors leading-tight">
                    {card.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed group-hover:text-white/70 transition-colors font-medium">
                    {card.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════
            SECTION 4 — Features Grid (Translucent overlay)
        ════════════════════════════════════════ */}
        <section className="py-24 relative overflow-hidden bg-transparent">
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="text-center mb-16">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {features.title}
              </h2>
              <p className="text-white/50 text-base md:text-lg max-w-3xl mx-auto leading-relaxed font-medium">
                {features.subtitle}
              </p>
            </div>

            {/* 4-column checkbox grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {features.items.map((item, i) => (
                <div
                  key={i}
                  className="border border-[#F2B135]/20 rounded-xl px-4 py-4 md:py-5 flex items-center gap-3 bg-white/[0.01] hover:border-[#F2B135] hover:bg-[#F2B135]/8 transition-all duration-300 group cursor-default"
                >
                  <FiCheckSquare
                    className="text-[#F2B135] flex-shrink-0 group-hover:scale-110 transition-transform"
                    size={20}
                  />
                  <span className="text-white text-xs md:text-sm font-semibold leading-tight">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════
            SECTION 5 — Collaborative Excellence
        ════════════════════════════════════════ */}
        <section className="py-24 relative bg-transparent">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2
                className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {excellence.title}
              </h2>
              <p className="text-white/60 text-base md:text-lg max-w-3xl mx-auto leading-relaxed font-medium">
                {excellence.subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {excellence.cards.map((card, i) => (
                <div
                  key={i}
                  className="group bg-white/[0.02] border border-white/[0.06] rounded-2xl p-8 hover:border-[#F2B135]/40 hover:bg-[#F2B135]/5 transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(0,0,0,0.5),0_0_30px_rgba(242,177,53,0.08)] text-center cursor-default"
                >
                  <div className="text-5xl text-[#F2B135] mb-6 group-hover:scale-110 transition-transform duration-300 flex justify-center">
                    {getIcon(card.icon, "w-12 h-12")}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4 group-hover:text-[#F2B135] transition-colors">
                    {card.title}
                  </h3>
                  <p className="text-[#F2B135]/65 text-sm leading-relaxed group-hover:text-[#F2B135]/90 transition-colors font-medium">
                    {card.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════
            SECTION 6 — Project Cycle (Vertical Timeline; Image 4 Style)
        ════════════════════════════════════════ */}
        <section className="py-24 relative overflow-hidden bg-transparent">
          <div className="max-w-5xl mx-auto px-6 relative z-10">
            <div className="text-center mb-20">
              <h2
                className="text-3xl md:text-5xl font-black text-white mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Our Project Process Cycle
              </h2>
              <p className="text-white/50 text-base md:text-lg max-w-2xl mx-auto font-medium">
                A proven, transparent process that delivers results on time, on budget, and beyond expectations.
              </p>
            </div>

            {/* Vertical timeline container */}
            <div className="relative">
              {/* Running vertical center line */}
              <div className="absolute left-4 md:left-1/2 top-2 bottom-2 w-[2px] bg-zinc-800 -translate-x-1/2 pointer-events-none" />
              <div className="absolute left-4 md:left-1/2 top-2 bottom-2 w-[2px] bg-gradient-to-b from-[#F2B135] via-[#ffaa00] to-[#F2B135] -translate-x-1/2 pointer-events-none opacity-40 animate-[gold-pulse_2s_infinite]" />

              {/* Steps list */}
              <div className="space-y-12">
                {projectCycle.map((step, i) => {
                  const isEven = i % 2 === 0;
                  return (
                    <div
                      key={i}
                      className="flex flex-col md:flex-row items-start relative group"
                    >
                      {/* Step node (circle) */}
                      <div className="absolute left-4 md:left-1/2 top-4 w-8 h-8 rounded-full bg-[#0c0c0c] border-2 border-[#F2B135] -translate-x-1/2 z-20 flex items-center justify-center text-xs font-black text-[#F2B135] shadow-[0_0_15px_rgba(242,177,53,0.3)] group-hover:bg-[#F2B135] group-hover:text-black transition-all duration-300">
                        {i + 1}
                      </div>

                      {/* Left Side Content Container */}
                      <div className="w-full md:w-1/2 md:pr-12 pl-12 md:pl-0 text-left md:text-right">
                        {isEven ? (
                          <div className="bg-white/[0.02] hover:bg-[#F2B135]/5 border border-white/10 rounded-2xl p-6 transition-all duration-300 hover:border-[#F2B135]/40 hover:-translate-y-1 shadow-xl">
                            <h3 className="text-[#F2B135] font-black text-lg mb-2 leading-snug">
                              {step.title}
                            </h3>
                            <p className="text-white/60 text-sm leading-relaxed font-medium">
                              {step.description}
                            </p>
                          </div>
                        ) : (
                          // Empty spacer for desktop layout alignment
                          <div className="hidden md:block h-10" />
                        )}
                      </div>

                      {/* Spacer line in middle */}
                      <div className="hidden md:block w-0" />

                      {/* Right Side Content Container */}
                      <div className="w-full md:w-1/2 md:pl-12 pl-12 md:pl-0 text-left">
                        {!isEven ? (
                          <div className="bg-white/[0.02] hover:bg-[#F2B135]/5 border border-white/10 rounded-2xl p-6 transition-all duration-300 hover:border-[#F2B135]/40 hover:-translate-y-1 shadow-xl">
                            <h3 className="text-[#F2B135] font-black text-lg mb-2 leading-snug">
                              {step.title}
                            </h3>
                            <p className="text-white/60 text-sm leading-relaxed font-medium">
                              {step.description}
                            </p>
                          </div>
                        ) : (
                          // Empty spacer for desktop layout alignment
                          <div className="hidden md:block h-10" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════
            SECTION 7 — Related Services
        ════════════════════════════════════════ */}
        <section className="py-24 relative bg-transparent">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F2B135]/30 bg-[#F2B135]/10 text-[#F2B135] text-xs font-bold uppercase mb-6">
                <span className="w-2 h-2 rounded-full bg-[#F2B135] animate-pulse" />
                Explore More
              </div>
              <h2
                className="text-3xl md:text-4xl font-black text-white mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Our Other Services
              </h2>
              <p className="text-white/50 text-lg max-w-2xl mx-auto font-medium">
                Discover more ways RojrzTech can help your business grow and thrive online.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedServices.map((service, i) => (
                <Link
                  key={i}
                  to={service.href}
                  className="group bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 hover:border-[#F2B135]/40 hover:bg-[#F2B135]/5 transition-all duration-300 hover:-translate-y-2 flex items-center justify-between"
                >
                  <div className="flex-1 mr-4">
                    <h3 className="text-white font-bold text-base group-hover:text-[#F2B135] transition-colors mb-1 leading-tight">
                      {service.name}
                    </h3>
                    <p className="text-white/40 text-xs leading-relaxed font-medium">{service.description}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full border border-[#F2B135]/20 group-hover:border-[#F2B135] group-hover:bg-[#F2B135] flex items-center justify-center flex-shrink-0 transition-all duration-300">
                    <FiArrowRight
                      className="text-[#F2B135] group-hover:text-black transition-colors"
                      size={16}
                    />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Home page components import */}
        <HomeBrandBanner />
        <VideoTestimonials />
        <Contact />
        <CTA />
        <Footer />
        <WhatsAppButton />
      </main>
    </div>
  );
};

export default ServicePageTemplate;
